/**
 * @typedef {Object} RemoteTransportConfig
 * @property {string} url - Endpoint URL to POST log entries to.
 * @property {number} [flushInterval=5000] - Milliseconds between batch flushes.
 * @property {number} [maxBatchSize=50] - Max entries per batch before forced flush.
 * @property {typeof globalThis.fetch} [fetchImpl] - Custom fetch (for testing).
 */

/**
 * Creates a remote log transport that batches entries and POSTs them
 * to a backend endpoint as JSON.
 *
 * The backend is expected to accept:
 *   POST {url}
 *   Content-Type: application/json
 *   Body: { entries: LogEntry[] }
 *
 * @param {RemoteTransportConfig} config
 * @returns {{ transport: import('./logger.js').LogTransport, flush: () => Promise<void>, destroy: () => void }}
 */
export function createRemoteTransport(config) {
  const {
    url,
    flushInterval = 5000,
    maxBatchSize = 50,
    fetchImpl = globalThis.fetch?.bind(globalThis),
  } = config;

  if (!url) throw new Error('remote transport: url is required');

  /**
   * JSON replacer that serializes Error objects (which stringify to `{}`).
   * @param {string} _key
   * @param {unknown} value
   * @returns {unknown}
   */
  function replacer(_key, value) {
    if (value instanceof Error) {
      return { name: value.name, message: value.message, stack: value.stack };
    }
    return value;
  }

  /** @type {import('./logger.js').LogEntry[]} */
  let buffer = [];
  let destroyed = false;

  /** @type {ReturnType<typeof setInterval> | null} */
  let timer = null;

  async function flush() {
    if (buffer.length === 0) return;
    const batch = buffer;
    buffer = [];

    try {
      await fetchImpl(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entries: batch }, replacer),
        keepalive: true,
      });
    } catch {
      // Network failure — entries are lost. This is acceptable for
      // frontend logging; the console output is the primary record.
    }
  }

  /** @param {import('./logger.js').LogEntry} entry */
  function transport(entry) {
    if (destroyed) return;
    buffer.push(entry);
    if (buffer.length >= maxBatchSize) {
      flush();
    }
  }

  timer = setInterval(flush, flushInterval);

  return {
    transport,
    flush,
    destroy() {
      destroyed = true;
      if (timer != null) {
        clearInterval(timer);
        timer = null;
      }
      flush();
    },
  };
}
